import * as React from "react";

import {
  GenericFormConsumerInterface,
  GenericReactProviderFormInterfaceOutput,
  useCustomerCartId,
  useCustomerSession,
  useForm,
  useProminentError,
} from "../../../Common";

import { useReactMyBagInitialState } from "../react-my-bag-initial-state-provider";
import {
  DataMutationMyBagGiftcardApply_Request,
  useDataMutationMyBagGiftcardApply_asController,
} from "../data-mutation--mybag-giftcard-apply";

import {
  ApplyAGiftcardForm,
  ApplyAGiftcardFormApp,
  ApplyAGiftcardFormLogic,
  initialApplyAGiftcardForm,
} from "./apply-a-giftcard-form";
import { MyBagPresentationOrder } from "../../model";
import {
  DataMutationMyBagGiftcardRemove_Request,
  useDataMutationMyBagGiftcardRemove_asController,
} from "../data-mutation--mybag-giftcard-remove";

interface ApplyAGiftcardFormConsumerInterface
  extends GenericFormConsumerInterface<ApplyAGiftcardForm> {
  applyAGiftcardForm: ApplyAGiftcardForm;

  applyTheGiftcard: () => void;
  removeAGiftcard: (
    theGiftcard: NonNullable<MyBagPresentationOrder["giftCardValueBreakdown"]>[number]
  ) => Promise<void>;
}

const defaultValue = {
  accessForm: initialApplyAGiftcardForm(),
} as unknown as ApplyAGiftcardFormConsumerInterface;

const ApplyAGiftcardFormContext = React.createContext(defaultValue);

export const ApplyAGiftcardFormProvider: React.FunctionComponent = (props) => {
  const { initialState } = useReactMyBagInitialState();
  const { setProminentError } = useProminentError();

  const { cartId } = useCustomerCartId();
  const { isCustomerLoggedIn } = useCustomerSession();
  const { mutateAsync: mutateGiftcardApply } =
    useDataMutationMyBagGiftcardApply_asController();
  const { mutateAsync: mutateGiftcardRemove } =
    useDataMutationMyBagGiftcardRemove_asController();

  const appContext = React.useMemo(
    () => ({
      initialState,
    }),
    [initialState]
  );
  const AppContext = React.useCallback(() => appContext, [appContext]);
  const thisForm = useForm(
    ApplyAGiftcardFormLogic,
    AppContext,
    initialApplyAGiftcardForm
  );
  const { form: applyAGiftcardForm, validateRightNowAndGiveMeAYesOrNo, resetForm } =
    thisForm;

  const applyTheGiftcard = React.useCallback(async () => {
    if (validateRightNowAndGiveMeAYesOrNo() && !!cartId) {
      try {
        if (!initialState.originCountryCode) {
          setProminentError(
            "The gift card service is not configured for this store."
          );
          return;
        }
        const request: DataMutationMyBagGiftcardApply_Request = {
          quote_id: cartId,
          giftcard_number: applyAGiftcardForm.giftcardNumber!,
          giftcard_pin: applyAGiftcardForm.giftcardPin!,
          country_code: initialState.originCountryCode,
          is_guest: !isCustomerLoggedIn,
        };
        await mutateGiftcardApply(request);
        resetForm();
      } catch (anyError) {
        console.error(anyError);
        if (anyError && (anyError as Error).message) {
          setProminentError((anyError as Error).message);
        }
        return;
      }
    } else {
      setProminentError(
        "Some information is missing or invalid. Check the red fields above for details."
      );
    }
  }, [
    validateRightNowAndGiveMeAYesOrNo,
    setProminentError,
    cartId,
    applyAGiftcardForm,
    isCustomerLoggedIn,
    mutateGiftcardApply,
    initialState,
  ]);

  const removeAGiftcard = React.useCallback(
    async (theGiftcard: NonNullable<MyBagPresentationOrder["giftCardValueBreakdown"]>[number]) => {
      try {
        const request: DataMutationMyBagGiftcardRemove_Request = {
          cart_id: cartId!,
          gift_card_code: theGiftcard?.privateBackendCode!,
        };
        await mutateGiftcardRemove(request);
      } catch (anyError) {
        console.error(anyError);
        if (anyError && (anyError as Error).message) {
          setProminentError((anyError as Error).message);
        }
        return;
      }
    },
    [cartId, mutateGiftcardRemove, setProminentError]
  );

  return (
    <ApplyAGiftcardFormContext.Provider
      value={{
        ...GenericReactProviderFormInterfaceOutput<
          ApplyAGiftcardForm,
          ApplyAGiftcardFormApp
        >(thisForm),

        applyAGiftcardForm,
        applyTheGiftcard,
        removeAGiftcard,
      }}
    >
      {props?.children || []}
    </ApplyAGiftcardFormContext.Provider>
  );
};

export const useApplyAGiftcardForm = () => {
  const context = React.useContext(ApplyAGiftcardFormContext);

  if (context === defaultValue) {
    throw new Error(
      "useApplyAGiftcardForm must be used within a ApplyAGiftcardFormProvider"
    );
  }

  return context;
};
